import { ChangeEvent, useState } from 'react';
import findKey from 'lodash/findKey';
import { useRouter } from 'next/router';

import IconExpandArrows from '@creditflowresearch/uilib/Icon/IconExpandArrows';
import IconCollapseArrows from '@creditflowresearch/uilib/Icon/IconCollapseArrows';
import { IQueryParams } from '@/creditflowresearch/domain/vo/IQueryParams';
import { ThemeType } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/themeTypes';
import Product from '@/creditflowresearch/domain/Product';
import SearchInput from '@/creditflowresearch/ui/common/components/molecules/SearchInput';
import { useArticlesContext } from '@/creditflowresearch/application/ArticlesAndStats/Articles/CreditFlowArticlesContext';
import { IColumn } from '@creditflowresearch/uilib/Table/DataTable/interfaces/Column';
import Button from '@creditflowresearch/uilib/Button';
import { Tile } from '@creditflowresearch/uilib/Tile';
import Flex from '@creditflowresearch/uilib/Flex';
import PageSubscriptionButton from '@/creditflowresearch/ui/components/creditFlow/articles/posts/PageSubscriptionButton';
import ArticlesTileHeader from './ArticlesTileHeader';
import ArticlesTabSelect from './ArticlesTabSelect';
import ArticlesTable from './ArticlesTable';
import { PricedDealsLinks } from '@/creditflowresearch/ui/components/creditFlow/articles/common/PricedDealsLinks';
import { getTrimmedString } from '@/creditflowresearch/ui/common/helpers/getTrimmedString';
import { getSubtypeTabsConfig, mapType } from './helpers';

import styles from './articles.module.scss';

interface IArticlesListProps {
  columns: IColumn[],
  title?: string,
  titlePrefix: string,
  tabsBasePath: string,
  filtersButton?: React.ReactNode,
}

const ArticlesList = ({
  columns,
  title: configTitle,
  titlePrefix,
  tabsBasePath,
  filtersButton,
}: IArticlesListProps) => {
  const { query: routeParams }:{ query: IQueryParams } = useRouter();
  const { articleType, markets, products } = routeParams;
  const {
    articlesData: { debouncedSearch, debouncedSearchString, totalCount, isFetching },
    isExpanded,
    setIsExpanded,
  } = useArticlesContext();

  const [search, setSearch] = useState(debouncedSearchString);

  const themeType: ThemeType = findKey(mapType, ({ url }) => url === articleType) as ThemeType;
  const tabs = getSubtypeTabsConfig({ productIds: Product.getIds(products), markets, type: themeType });
  const defaultTitle = configTitle || 'Commentary & Deal Flow';
  const title = themeType && themeType !== ThemeType.all ? mapType[themeType].label : defaultTitle;
  const titleWithPrefix = titlePrefix + title;

  const onSearchChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>): void => {
    setSearch(value);
    const filteredValue = getTrimmedString(value);
    if (filteredValue === null) return;
    debouncedSearch(filteredValue);
  };

  const toggleExpand = (): void => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Tile dataTest="commentaryDealFlow">
      <ArticlesTileHeader
        title={ tabs.length > 0 ? title : `All ${ titleWithPrefix }` }
        tabs={ tabs }
        basePath={ tabsBasePath }
      >
        <Flex
          direction={ { sm: 'column-reverse', md: 'column-reverse', xl: 'row' } }
          align={ { sm: 'stretch', md: 'stretch', xl: 'flex-start' } }
          justify={ { xl: 'flex-end' } }
          gap={ 10 }
          className={ tabs.length > 0 ? styles.controlsFlex : '' }
        >
          <SearchInput
            onChange={ onSearchChange }
            value={ search }
            placeholder="Filter by Headline or Description"
            className={ styles.searchInputField }
            dataTest="formSearchInput"
          />
          { tabs.length > 0 && (
            <div className={ styles.tabSelect }>
              <ArticlesTabSelect tabs={ tabs } basePath={ tabsBasePath } />
            </div>
          ) }
          <Flex align="center" gap={ 10 } wrap="wrap">
            <PageSubscriptionButton />
            <PricedDealsLinks />
          </Flex>
          { filtersButton }
          <div className={ styles.expandCollapseWrapper }>
            <Button
              onClick={ toggleExpand }
              size="S"
              variant="outline"
              icon={ isExpanded ? <IconCollapseArrows /> : <IconExpandArrows /> }
              disabled={ !totalCount || isFetching }
              dataTest="expandCollapseIcon"
            />
          </div>
        </Flex>
      </ArticlesTileHeader>
      <ArticlesTable columns={ columns } />
    </Tile>
  );
};

export default ArticlesList;
